import type React from "react";
import { useMemo } from "react";

import { areIntervalsOverlapping, parseISO, set } from "date-fns";
import { useTranslation } from "react-i18next";

import type { AppointmentV1 } from "api/services/appointment/schemas/appointment_v1";
import { Notification } from "shared/atoms/Notification";

interface Props {
  appointmentsV1: AppointmentV1[];
  date: Date | null;
  startTime: Date | null;
  endTime: Date | null;
}

export const DoubleBookingWarning: React.FC<Props> = ({ date, startTime, endTime, appointmentsV1 }) => {
  const { t } = useTranslation();

  const isDoubleBooked = useMemo(() => {
    if (!date || !startTime || !endTime) return false;

    const start = set(date, {
      hours: startTime.getHours(),
      minutes: startTime.getMinutes(),
      seconds: 0,
      milliseconds: 0,
    });
    const end = set(date, { hours: endTime.getHours(), minutes: endTime.getMinutes(), seconds: 0, milliseconds: 0 });

    return appointmentsV1.some((call: AppointmentV1) =>
      areIntervalsOverlapping({ start, end }, { start: parseISO(call.start_time), end: parseISO(call.end_time) })
    );
  }, [date, startTime, endTime, appointmentsV1]);

  if (!isDoubleBooked) return null;

  return <Notification variant="warning">{t("messages.appointments.double_booking_warning")}</Notification>;
};
